import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getChildrenItems } from '../services/getChildrenItems';
import { useQwanyx } from 'qwanyx';
import { StackOperations } from '../services/stackOperations';
import { useStackDragDrop } from '../hooks/useStackDragDrop';
import { 
    transformToTreeData, 
    getBackgroundColor, 
    getBorderStyle, 
    createCardData 
} from '../utils/stackUtils';

export const StackItem = ({
    icon,
    text: initialText,
    isFolder = false,
    level = 0,
    selected = false,
    selectedItems = [],
    onSelect,
    itemId,
    isRoot = false,
    registerItem = null,
    itemData = null,
    onRename = null,
    onDrop = null,
    dragCompletedSignal = 0,
    rows = 50,
    disableCurrentCardUpdate = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [children, setChildren] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [localItemData, setLocalItemData] = useState(itemData);
    const [isEditing, setIsEditing] = useState(false);
    const [displayText, setDisplayText] = useState(initialText);
    const [editText, setEditText] = useState(initialText);
    // FIXED: Track last refresh signal to avoid duplicate fetches
    const [lastRefreshSignal, setLastRefreshSignal] = useState(0);
    
    const inputRef = useRef(null);
    const iconRef = useRef(null);

    // Get Qwanyx context for card update functionality
    const { updateCardByName, showCard } = useQwanyx();

    // Initialize stack operations
    const stackOps = new StackOperations(false); // debug handled at higher level

    // Use drag and drop hook
    const {
        isHovering,
        isHoveringOverIcon,
        handleDragStart,
        handleDragEnd,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop
    } = useStackDragDrop(stackOps, onDrop, false);

    // Memoize fetchChildren to safely use in useEffect dependencies
    const fetchChildren = useCallback(async () => {
        if (!itemId) return;

        setIsLoading(true);
        try {
            const childItems = await getChildrenItems(itemId);

            childItems.forEach(item => {
                if (item._id === itemId && !localItemData) {
                    setLocalItemData(item);
                }
            });

            const childrenItems = transformToTreeData(childItems);
            setChildren(childrenItems);
            
            // FIXED: Debug logging for root fetches
            if (isRoot) {
                console.log(`[StackItem] Root fetched ${childItems.length} children:`, childItems.map(c => c.title || c._id));
            }
        } catch (error) {
            console.error("Error fetching children:", error);
        } finally {
            setIsLoading(false);
        }
    }, [itemId, localItemData, isRoot]);

    useEffect(() => {
        setDisplayText(initialText);
    }, [initialText]);

    useEffect(() => {
        if (registerItem && itemId && !isRoot) {
            registerItem(itemId, level, localItemData);
        }
    }, [registerItem, itemId, level, isRoot, localItemData]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    // FIXED: Initial fetch for root - always fetch for root, regardless of folder status
    useEffect(() => {
        if (isRoot) {
            fetchChildren();
            return;
        }

        if (isFolder && isOpen && children.length === 0) {
            fetchChildren();
        }
    }, [isRoot, isFolder, isOpen, children.length, fetchChildren]);

    // FIXED: Enhanced refresh logic for drag signals
    useEffect(() => {
        if (dragCompletedSignal === 0 || dragCompletedSignal === lastRefreshSignal) {
            return; // Skip if no signal or same signal as last time
        }

        console.log(`[StackItem] Drag signal received: ${dragCompletedSignal}, last: ${lastRefreshSignal}, isRoot: ${isRoot}, itemId: ${itemId}`);
        
        // Update last refresh signal to avoid duplicate fetches
        setLastRefreshSignal(dragCompletedSignal);

        // FIXED: For root items, always refresh when drag signal changes
        if (isRoot) {
            console.log(`[StackItem] Root refreshing due to drag signal: ${dragCompletedSignal}`);
            fetchChildren();
            return;
        }

        // For non-root folders, refresh if they're open
        if (itemId && isFolder && isOpen) {
            console.log(`[StackItem] Folder ${itemId} refreshing due to drag signal`);
            fetchChildren();
        }
    }, [dragCompletedSignal, isRoot, itemId, isFolder, isOpen, fetchChildren, lastRefreshSignal]);

    const toggleFolder = (e) => {
        if (isFolder || isRoot) {
            e.stopPropagation();
            setIsOpen(!isOpen);
        }
    };

    const updateCurrentCard = () => {
        if (!itemId) return;

        const cardData = createCardData(localItemData, itemId, displayText);
        updateCardByName("currentCard", cardData);
        showCard("currentCard");
    };

    const handleClick = (e) => {
        if (document.querySelector('.dragging')) return;

        if (!isRoot && onSelect) {
            onSelect(itemId, e.shiftKey, e.ctrlKey || e.metaKey);

            if (!e.shiftKey && !e.ctrlKey && !e.metaKey && !disableCurrentCardUpdate) {
                updateCurrentCard();
            }
        }
    };

    const handleDoubleClick = (e) => {
        e.stopPropagation();
        if (!isRoot) {
            setIsEditing(true);
            setEditText(displayText);
        }
    };

    const handleInputChange = (e) => {
        setEditText(e.target.value);
    };

    const handleInputBlur = () => {
        finishEditing();
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            finishEditing();
        } else if (e.key === 'Escape') {
            cancelEditing();
        }
    };

    const finishEditing = async () => {
        setIsEditing(false);

        if (editText !== displayText && editText.trim() !== '') {
            setDisplayText(editText);

            if (localItemData) {
                const updatedItemData = {
                    ...localItemData,
                    title: editText
                };
                setLocalItemData(updatedItemData);
            }

            try {
                await stackOps.updateItemTitle(itemId, editText);

                if (!disableCurrentCardUpdate) {
                    updateCurrentCard();
                }
            } catch (error) {
                console.error("Failed to update title:", error);
            }

            if (onRename) {
                onRename(itemId, editText);
            }
        } else {
            setEditText(displayText);
        }
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setEditText(displayText);
    };

    const isSelected = selectedItems.includes(itemId);

    if (isRoot) {
        const hasMoreChildren = children.length > rows;

        return (
            <div style={{ width: '100%', overflow: 'auto' }}>
                {isLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
                ) : hasMoreChildren ? (
                    <div style={{ maxHeight: `${rows * 50}px`, overflow: 'auto' }}>
                        {children.map((child) => (
                            <StackItem
                                key={child.id}
                                itemId={child.id}
                                icon={child.icon}
                                text={child.text}
                                isFolder={child.isFolder}
                                selectedItems={selectedItems}
                                onSelect={onSelect}
                                registerItem={registerItem}
                                itemData={child.itemData}
                                onRename={onRename}
                                onDrop={onDrop}
                                dragCompletedSignal={dragCompletedSignal}
                                rows={rows}
                                disableCurrentCardUpdate={disableCurrentCardUpdate}
                            />
                        ))}
                    </div>
                ) : children.length > 0 ? (
                    children.map((child) => (
                        <StackItem
                            key={child.id}
                            itemId={child.id}
                            icon={child.icon}
                            text={child.text}
                            isFolder={child.isFolder}
                            selectedItems={selectedItems}
                            onSelect={onSelect}
                            registerItem={registerItem}
                            itemData={child.itemData}
                            onRename={onRename}
                            onDrop={onDrop}
                            dragCompletedSignal={dragCompletedSignal}
                            rows={rows}
                            disableCurrentCardUpdate={disableCurrentCardUpdate}
                        />
                    ))
                ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                        No items available
                    </div>
                )}
            </div>
        );
    }

    const folderIcon = isFolder
        ? isOpen
            ? 'fa-folder-open'
            : 'fa-folder'
        : icon;

    const hasMoreFolderChildren = children.length > rows;

    return (
        <>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px',
                    paddingLeft: `${10 + level * 20}px`,
                    borderBottom: getBorderStyle(isHovering, isFolder),
                    cursor: 'pointer',
                    backgroundColor: getBackgroundColor(isSelected, isHovering),
                    transition: 'all 0.2s ease',
                    position: 'relative'
                }}
                onClick={handleClick}
                draggable={!isRoot}
                onDragStart={(e) => handleDragStart(e, itemId, isFolder, displayText, localItemData, isRoot)}
                onDragEnd={handleDragEnd}
                onDragEnter={(e) => handleDragEnter(e, iconRef, isRoot)}
                onDragOver={(e) => handleDragOver(e, iconRef, isRoot)}
                onDragLeave={(e) => handleDragLeave(e, iconRef)}
                onDrop={(e) => handleDrop(e, itemId, isFolder, iconRef, localItemData, fetchChildren)}
                className="stack-item"
            >
                {isHovering && (
                    <div
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            backgroundColor: isFolder ? '#1890ff' : '#40a9ff'
                        }}
                    />
                )}
                <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {isFolder ? (
                        <i
                            ref={iconRef}
                            className={`fas ${folderIcon} small`}
                            onClick={toggleFolder}
                            style={{
                                cursor: 'pointer',
                                color: isHoveringOverIcon ? '#2ecc71' : 'inherit',
                                transition: 'color 0.2s ease'
                            }}
                        />
                    ) : (
                        <i
                            ref={iconRef}
                            className={`fas ${icon} small`}
                            style={{
                                color: isHoveringOverIcon ? '#2ecc71' : 'inherit',
                                transition: 'color 0.2s ease'
                            }}
                        />
                    )}

                    {isEditing ? (
                        <div style={{ marginLeft: '8px', width: '100%' }}>
                            <input
                                ref={inputRef}
                                type="text"
                                value={editText}
                                className="input is-small"
                                onChange={handleInputChange}
                                onBlur={handleInputBlur}
                                onKeyDown={handleInputKeyDown}
                                style={{ width: '100%' }}
                                onClick={(e) => e.stopPropagation()}
                                onDoubleClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    ) : (
                        <span
                            style={{ marginLeft: '8px', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            onDoubleClick={handleDoubleClick}
                        >
                            {displayText}
                        </span>
                    )}
                </span>
                {isFolder && isLoading && (
                    <span style={{ fontSize: '12px', color: '#999' }}>Loading...</span>
                )}
            </div>

            {isOpen && isFolder && children.length > 0 && hasMoreFolderChildren && (
                <div style={{ maxHeight: `${rows * 50}px`, overflow: 'auto' }}>
                    {children.map((child) => (
                        <StackItem
                            key={child.id}
                            icon={child.icon}
                            text={child.text}
                            isFolder={child.isFolder}
                            level={level + 1}
                            itemId={child.id}
                            selectedItems={selectedItems}
                            onSelect={onSelect}
                            registerItem={registerItem}
                            itemData={child.itemData}
                            onRename={onRename}
                            onDrop={onDrop}
                            dragCompletedSignal={dragCompletedSignal}
                            rows={rows}
                            disableCurrentCardUpdate={disableCurrentCardUpdate}
                        />
                    ))}
                </div>
            )}

            {isOpen && isFolder && children.length > 0 && !hasMoreFolderChildren && children.map((child) => (
                <StackItem
                    key={child.id}
                    icon={child.icon}
                    text={child.text}
                    isFolder={child.isFolder}
                    level={level + 1}
                    itemId={child.id}
                    selectedItems={selectedItems}
                    onSelect={onSelect}
                    registerItem={registerItem}
                    itemData={child.itemData}
                    onRename={onRename}
                    onDrop={onDrop}
                    dragCompletedSignal={dragCompletedSignal}
                    rows={rows}
                    disableCurrentCardUpdate={disableCurrentCardUpdate}
                />
            ))}

            {isOpen && isFolder && children.length === 0 && !isLoading && (
                <div style={{
                    padding: '10px',
                    paddingLeft: `${10 + (level + 1) * 20}px`,
                    color: '#999',
                    fontStyle: 'italic'
                }}>
                    No items
                </div>
            )}
        </>
    );
};

export default StackItem;